import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Typography, TextField, Button, Grid, Switch, FormControlLabel, CircularProgress, Chip, Autocomplete, Avatar } from '@mui/material';
import { Save as SaveIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { AdminPageContainer, StaticCard } from '../../../components/admin/sharedStyles';
import PageHeader from '../../../components/PageHeader';
import ConfigSubMenu from '../../../components/admin/ConfigSubMenu';
import AppTimePicker from '../../../components/AppTimePicker';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { getConfigValue, saveConfig } from '../../../redux/slices/config';
import { getServiceAccounts } from '../../../redux/ducks/serviceAccount';
import { getManagers } from '../../../redux/slices/users';
import { getAvatarUrl } from '../../../utils/avatarUtils';
import { TIMEZONES } from '../../../utils/timezones';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';

const DEFAULT_BUSINESS_HOURS = { timezone: 'America/New_York', start: '09:00', end: '17:00', workingDays: [1, 2, 3, 4, 5] };

const DEFAULT_EOD_LEAD_MINUTES = 30;
const MIN_EOD_LEAD_MINUTES = 10;
const MAX_EOD_LEAD_MINUTES = 240;

const DEFAULT_ESCALATION = {
    enabled: false,
    acknowledgeAfterMinutes: '30',
    escalateAfterMinutes: '120',
    pmNudgeIntervalMinutes: '30',
    clientUpdateIntervalMinutes: '60',
    maxClientPosts: '8',
    postingServiceAccount: null,
    escalationRecipients: [],
    acknowledgementTemplate: '',
    statusUpdateTemplate: '',
    eodChecklistEnabled: false,
    eodChecklistLeadMinutes: String(DEFAULT_EOD_LEAD_MINUTES),
    eodDigestEnabled: false,
};

const DAY_OPTIONS = [
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
    { value: 0, label: 'Sun' },
];

const MINUTE_PRESETS = [
    { label: '15m', minutes: 15 },
    { label: '30m', minutes: 30 },
    { label: '1h', minutes: 60 },
    { label: '2h', minutes: 120 },
    { label: '4h', minutes: 240 },
];

const PLACEHOLDER_TOKENS = [
    { token: '{author.name}', description: 'The first name of the client who wrote the comment' },
    { token: '{client.name}', description: "The client company's name" },
    { token: '{project.name}', description: "The project's name" },
    { token: '{pm.name}', description: "The assigned PM's name" },
    { token: '{time}', description: 'The time this update is posted' },
    { token: '{nextUpdate}', description: 'When the next automated update will post' },
    { token: '{escalatedTo}', description: 'The admin the thread was escalated to' },
    { token: '{escalatedAt}', description: 'The time the thread was escalated' },
];

const personName = (person) => {
    if (!person) return '';
    const name = `${person.first_name || ''} ${person.last_name || ''}`.trim();
    return name || person.fullName || '';
};

const positiveIntegerError = (value, unitLabel, singularLabel) => {
    const raw = String(value ?? '').trim();
    if (!raw) return `Enter a whole number of ${unitLabel}`;
    if (!/^\d+$/.test(raw)) return `Use a whole number of ${unitLabel} — no decimals or negative values`;
    if (parseInt(raw, 10) < 1) return `Must be at least 1 ${singularLabel}`;
    return '';
};

const parseIntOrDefault = (val, def, min = 0) => {
    const n = parseInt(val, 10);
    return Number.isFinite(n) && n >= min ? n : def;
};

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const SectionCard = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const SwitchNote = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const SwitchError = styled(Typography)(({ theme }) => ({
    display: 'block',
    marginBottom: theme.spacing(2),
}));

const ActionsRow = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
});

const ChipRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
}));

const SelectableChip = styled(Chip, { shouldForwardProp: (prop) => prop !== 'selected' })(({ theme, selected }) => ({
    cursor: 'pointer',
    backgroundColor: selected ? theme.palette.primary.main : 'transparent',
    color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
    border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.text.secondary}`,
    '&:hover': {
        backgroundColor: selected ? theme.palette.primary.dark : theme.palette.action.hover,
    },
}));

const MinuteFieldBlock = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const FieldDescription = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const MinuteTextField = styled(TextField)({
    width: '100%',
    maxWidth: 320,
});

const TokenCard = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
}));

const TokenRow = styled('button')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    margin: 0,
    padding: theme.spacing(0.5, 1),
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    background: 'none',
    font: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
    },
    '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
    },
}));

const TokenCode = styled(Typography)(({ theme }) => ({
    fontFamily: 'monospace',
    color: theme.palette.mode === 'light' ? theme.palette.primary.dark : theme.palette.primary.main,
}));

const OptionRow = styled('li')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const OptionAvatar = styled(Avatar)({
    width: 24,
    height: 24,
});

const MinuteField = ({ id, label, value, onChange, description, error, disabled, min = 1 }) => (
    <MinuteFieldBlock>
        <Typography variant="subtitle2" id={id} gutterBottom>
            {label}
        </Typography>
        <FieldDescription variant="body2" color="text.secondary">
            {description}
        </FieldDescription>
        <ChipRow role="group" aria-labelledby={id}>
            {MINUTE_PRESETS.filter((p) => p.minutes >= min).map((p) => (
                <SelectableChip key={p.label} label={p.label} disabled={disabled} selected={String(p.minutes) === String(value)} onClick={() => onChange(String(p.minutes))} />
            ))}
        </ChipRow>
        <MinuteTextField
            type="number"
            label={`${label} (minutes)`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            inputProps={{ min, step: 1 }}
            disabled={disabled}
            error={!!error}
            helperText={error || ''}
        />
    </MinuteFieldBlock>
);

const EscalationSettings = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [serviceAccountsLoading, setServiceAccountsLoading] = useState(true);
    const [recipientsLoading, setRecipientsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [businessHours, setBusinessHours] = useState(DEFAULT_BUSINESS_HOURS);
    const [escalation, setEscalation] = useState(DEFAULT_ESCALATION);
    const [serviceAccounts, setServiceAccounts] = useState([]);
    const [userOptions, setUserOptions] = useState([]);

    useEffect(() => {
        loadAll();
    }, []);

    const loadAll = () => {
        setLoading(true);
        setServiceAccountsLoading(true);
        setRecipientsLoading(true);
        let pending = 2;
        const done = () => {
            pending -= 1;
            if (pending <= 0) setLoading(false);
        };

        dispatch(
            getConfigValue({
                key: 'systemConfig',
                value: 'businessHours',
                callback: (data) => {
                    const val = data?.value;
                    if (val && typeof val === 'object') {
                        setBusinessHours({
                            timezone: val.timezone || DEFAULT_BUSINESS_HOURS.timezone,
                            start: val.start || DEFAULT_BUSINESS_HOURS.start,
                            end: val.end || DEFAULT_BUSINESS_HOURS.end,
                            workingDays: Array.isArray(val.workingDays) ? val.workingDays : DEFAULT_BUSINESS_HOURS.workingDays,
                        });
                    } else {
                        setBusinessHours(DEFAULT_BUSINESS_HOURS);
                    }
                    done();
                },
            }),
        );

        dispatch(
            getConfigValue({
                key: 'escalationConfig',
                value: null,
                callback: (data) => {
                    const val = data?.value;
                    if (val && typeof val === 'object') {
                        setEscalation({
                            enabled: !!val.enabled,
                            acknowledgeAfterMinutes: val.acknowledgeAfterMinutes != null ? String(val.acknowledgeAfterMinutes) : DEFAULT_ESCALATION.acknowledgeAfterMinutes,
                            escalateAfterMinutes: val.escalateAfterMinutes != null ? String(val.escalateAfterMinutes) : DEFAULT_ESCALATION.escalateAfterMinutes,
                            pmNudgeIntervalMinutes: val.pmNudgeIntervalMinutes != null ? String(val.pmNudgeIntervalMinutes) : DEFAULT_ESCALATION.pmNudgeIntervalMinutes,
                            clientUpdateIntervalMinutes:
                                val.clientUpdateIntervalMinutes != null ? String(val.clientUpdateIntervalMinutes) : DEFAULT_ESCALATION.clientUpdateIntervalMinutes,
                            maxClientPosts: val.maxClientPosts != null ? String(val.maxClientPosts) : DEFAULT_ESCALATION.maxClientPosts,
                            postingServiceAccount: val.postingServiceAccount || null,
                            escalationRecipients: Array.isArray(val.escalationRecipients) ? val.escalationRecipients.map((id) => String(id)) : [],
                            acknowledgementTemplate: val.acknowledgementTemplate || '',
                            statusUpdateTemplate: val.statusUpdateTemplate || '',
                            eodChecklistEnabled: !!val.eodChecklistEnabled,
                            eodChecklistLeadMinutes: val.eodChecklistLeadMinutes != null ? String(val.eodChecklistLeadMinutes) : DEFAULT_ESCALATION.eodChecklistLeadMinutes,
                            eodDigestEnabled: !!val.eodDigestEnabled,
                        });
                    } else {
                        setEscalation(DEFAULT_ESCALATION);
                    }
                    done();
                },
            }),
        );

        dispatch(
            getServiceAccounts((data) => {
                setServiceAccounts(Array.isArray(data) ? data.filter((a) => a.active !== false) : []);
                setServiceAccountsLoading(false);
            }),
        );

        dispatch(
            getManagers({
                callback: (data) => {
                    const raw = Array.isArray(data) ? data : [];
                    setUserOptions(raw.filter((u) => u?.active !== false && !u?.isDeleted && u?.client?.isInternal === true));
                    setRecipientsLoading(false);
                },
            }),
        );
    };

    const { showPrompt, confirmLeave, cancelLeave, message: unsavedMessage } = useUnsavedChanges(isDirty);

    const setBusinessHoursField = (field, value) => {
        setIsDirty(true);
        setBusinessHours((prev) => ({ ...prev, [field]: value }));
    };

    const setEscalationField = (field, value) => {
        setIsDirty(true);
        setEscalation((prev) => ({ ...prev, [field]: value }));
    };

    const toggleWorkingDay = (day) => {
        setIsDirty(true);
        setBusinessHours((prev) => {
            const has = prev.workingDays.includes(day);
            const workingDays = has ? prev.workingDays.filter((d) => d !== day) : [...prev.workingDays, day];
            return { ...prev, workingDays };
        });
    };

    const startEndValid = businessHours.start && businessHours.end && businessHours.start < businessHours.end;
    const workingDaysValid = businessHours.workingDays.length > 0;
    const businessHoursValid = startEndValid && workingDaysValid;

    const escalationDisabled = !escalation.enabled;

    const timingErrors = useMemo(() => {
        if (escalationDisabled) return {};
        const errors = {
            acknowledgeAfterMinutes: positiveIntegerError(escalation.acknowledgeAfterMinutes, 'minutes', 'minute'),
            escalateAfterMinutes: positiveIntegerError(escalation.escalateAfterMinutes, 'minutes', 'minute'),
            pmNudgeIntervalMinutes: positiveIntegerError(escalation.pmNudgeIntervalMinutes, 'minutes', 'minute'),
            clientUpdateIntervalMinutes: positiveIntegerError(escalation.clientUpdateIntervalMinutes, 'minutes', 'minute'),
            maxClientPosts: positiveIntegerError(escalation.maxClientPosts, 'posts', 'post'),
        };
        if (!errors.acknowledgeAfterMinutes && !errors.escalateAfterMinutes && parseInt(escalation.escalateAfterMinutes, 10) <= parseInt(escalation.acknowledgeAfterMinutes, 10)) {
            errors.escalateAfterMinutes = 'Must be longer than the acknowledgement wait, since both are measured from the client comment';
        }
        return errors;
    }, [escalationDisabled, escalation]);

    const completenessErrors = useMemo(() => {
        if (escalationDisabled) return {};
        return {
            postingServiceAccount: escalation.postingServiceAccount ? '' : 'Choose the account whose name appears on automated comments',
            escalationRecipients: escalation.escalationRecipients.length > 0 ? '' : 'Choose at least one person to notify when a comment escalates',
            acknowledgementTemplate: escalation.acknowledgementTemplate.trim() ? '' : 'Write the message clients see when a comment is acknowledged',
            statusUpdateTemplate: escalation.statusUpdateTemplate.trim() ? '' : 'Write the message clients see on each status update',
        };
    }, [escalationDisabled, escalation]);

    const eodErrors = useMemo(() => {
        const errors = { eodChecklistLeadMinutes: '', eodDigestEnabled: '' };
        if (escalation.eodChecklistEnabled) {
            const raw = String(escalation.eodChecklistLeadMinutes ?? '').trim();
            const value = parseInt(raw, 10);
            if (!/^\d+$/.test(raw)) {
                errors.eodChecklistLeadMinutes = 'Enter a whole number of minutes — no decimals or negative values';
            } else if (value < MIN_EOD_LEAD_MINUTES) {
                errors.eodChecklistLeadMinutes = `Use ${MIN_EOD_LEAD_MINUTES} minutes or more. Pollux only checks who is close to finishing every 5 minutes, so a shorter lead time is usually stepped over and no checklist is sent at all`;
            } else if (value > MAX_EOD_LEAD_MINUTES) {
                errors.eodChecklistLeadMinutes = `Use ${MAX_EOD_LEAD_MINUTES} minutes (4 hours) or less, so the checklist still arrives near the end of the day`;
            }
        }
        if (escalation.eodDigestEnabled && escalation.escalationRecipients.length === 0) {
            errors.eodDigestEnabled = 'Add at least one person under Escalation recipients above — the digest is sent to that same list';
        }
        return errors;
    }, [escalation.eodChecklistEnabled, escalation.eodChecklistLeadMinutes, escalation.eodDigestEnabled, escalation.escalationRecipients]);

    const hasTimingErrors = Object.values(timingErrors).some(Boolean);
    const hasCompletenessErrors = Object.values(completenessErrors).some(Boolean);
    const hasEodErrors = Object.values(eodErrors).some(Boolean);

    const recipientsDisabled = escalationDisabled && !escalation.eodDigestEnabled;

    const recipientsById = useMemo(() => new Map(userOptions.map((u) => [String(u._id), u])), [userOptions]);

    const selectedRecipients = useMemo(
        () => escalation.escalationRecipients.map((id) => recipientsById.get(String(id)) || { _id: String(id), unlisted: true }),
        [recipientsById, escalation.escalationRecipients],
    );

    const recipientOptions = useMemo(() => [...userOptions, ...selectedRecipients.filter((u) => u.unlisted)], [userOptions, selectedRecipients]);

    const recipientLabel = (option) => {
        if (!option) return '';
        if (option.unlisted) return recipientsLoading ? 'Loading…' : 'User no longer listed';
        return personName(option) || 'Unnamed user';
    };

    const selectedServiceAccount = useMemo(
        () => serviceAccounts.find((a) => a._id === escalation.postingServiceAccount) || null,
        [serviceAccounts, escalation.postingServiceAccount],
    );

    const handleCopyToken = (token) => {
        Promise.resolve()
            .then(() => navigator.clipboard.writeText(token))
            .then(() => dispatch(showAlert({ severity: 'info', message: `Copied ${token} to clipboard` })))
            .catch(() => dispatch(showAlert({ severity: 'error', message: `Could not copy ${token}. Select the text and copy it manually.` })));
    };

    const handleSave = () => {
        if (!businessHoursValid) {
            dispatch(
                showAlert({
                    severity: 'error',
                    message: 'Fix the business hours settings before saving: the start time must be before the end time and at least one working day must be selected.',
                }),
            );
            return;
        }

        if (hasTimingErrors) {
            dispatch(showAlert({ severity: 'error', message: 'Fix the highlighted timing fields before saving. Each needs a whole number of at least 1, and the escalation wait must be longer than the acknowledgement wait.' }));
            return;
        }

        if (hasEodErrors) {
            dispatch(
                showAlert({
                    severity: 'error',
                    message: `Fix the end-of-day checklist settings before saving. The lead time must be a whole number between ${MIN_EOD_LEAD_MINUTES} and ${MAX_EOD_LEAD_MINUTES} minutes, and the digest needs at least one escalation recipient.`,
                }),
            );
            return;
        }

        if (hasCompletenessErrors) {
            dispatch(
                showAlert({
                    severity: 'error',
                    message: 'Automatic escalation cannot be turned on until a posting account, at least one recipient, and both message templates are set.',
                }),
            );
            return;
        }

        setSaving(true);
        let pending = 2;
        let hadError = false;
        const done = (data) => {
            if (data?.status === 0) hadError = true;
            pending -= 1;
            if (pending <= 0) {
                setSaving(false);
                if (!hadError) {
                    setIsDirty(false);
                    dispatch(showAlert({ severity: 'success', message: 'Escalation settings saved successfully' }));
                }
            }
        };

        dispatch(saveConfig({ key: 'systemConfig', field: 'businessHours', value: businessHours, callback: done }));

        dispatch(
            saveConfig({
                key: 'escalationConfig',
                value: {
                    enabled: escalation.enabled,
                    acknowledgeAfterMinutes: parseIntOrDefault(escalation.acknowledgeAfterMinutes, DEFAULT_ESCALATION.acknowledgeAfterMinutes, 1),
                    escalateAfterMinutes: parseIntOrDefault(escalation.escalateAfterMinutes, DEFAULT_ESCALATION.escalateAfterMinutes, 1),
                    pmNudgeIntervalMinutes: parseIntOrDefault(escalation.pmNudgeIntervalMinutes, DEFAULT_ESCALATION.pmNudgeIntervalMinutes, 1),
                    clientUpdateIntervalMinutes: parseIntOrDefault(escalation.clientUpdateIntervalMinutes, DEFAULT_ESCALATION.clientUpdateIntervalMinutes, 1),
                    maxClientPosts: parseIntOrDefault(escalation.maxClientPosts, DEFAULT_ESCALATION.maxClientPosts, 1),
                    postingServiceAccount: escalation.postingServiceAccount || null,
                    escalationRecipients: escalation.escalationRecipients,
                    acknowledgementTemplate: escalation.acknowledgementTemplate,
                    statusUpdateTemplate: escalation.statusUpdateTemplate,
                    eodChecklistEnabled: escalation.eodChecklistEnabled,
                    eodChecklistLeadMinutes: parseIntOrDefault(escalation.eodChecklistLeadMinutes, DEFAULT_EOD_LEAD_MINUTES, MIN_EOD_LEAD_MINUTES),
                    eodDigestEnabled: escalation.eodDigestEnabled,
                },
                callback: done,
            }),
        );
    };

    return (
        <AdminPageContainer>
            <PageHeader title="Escalation" subtitle="Control when unanswered client comments get an automatic public acknowledgement and when they escalate to the team." />

            <ConfigSubMenu />

            {loading ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : (
                <>
                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Business hours
                        </Typography>
                        <SectionSubtitle variant="body2" color="text.secondary">
                            The escalation clock only runs during these hours. A comment posted outside of them starts counting down at the next working hour.
                        </SectionSubtitle>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    value={businessHours.timezone}
                                    onChange={(_, value) => setBusinessHoursField('timezone', value || DEFAULT_BUSINESS_HOURS.timezone)}
                                    options={TIMEZONES}
                                    disableClearable
                                    renderInput={(params) => <TextField {...params} label="Timezone" helperText="The timezone business hours are measured in" />}
                                />
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <AppTimePicker
                                    label="Start"
                                    value={businessHours.start}
                                    onChange={(v) => setBusinessHoursField('start', v)}
                                    error={!startEndValid}
                                    helperText={!startEndValid ? 'Start must be before end' : 'Workday start time'}
                                />
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <AppTimePicker
                                    label="End"
                                    value={businessHours.end}
                                    onChange={(v) => setBusinessHoursField('end', v)}
                                    error={!startEndValid}
                                    helperText={!startEndValid ? 'End must be after start' : 'Workday end time'}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" id="working-days-label" gutterBottom>
                                    Working days
                                </Typography>
                                <ChipRow role="group" aria-labelledby="working-days-label">
                                    {DAY_OPTIONS.map((d) => (
                                        <SelectableChip
                                            key={d.value}
                                            label={d.label}
                                            selected={businessHours.workingDays.includes(d.value)}
                                            onClick={() => toggleWorkingDay(d.value)}
                                        />
                                    ))}
                                </ChipRow>
                                {!workingDaysValid && (
                                    <Typography variant="caption" color="error">
                                        Select at least one working day
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Timing
                        </Typography>
                        <SectionSubtitle variant="body2" color="text.secondary">
                            Controls whether escalation runs at all, who posts the automated comments, and how long each stage waits (measured in business minutes, per the hours
                            above).
                        </SectionSubtitle>
                        <FormControlLabel
                            control={<Switch checked={escalation.enabled} onChange={(e) => setEscalationField('enabled', e.target.checked)} />}
                            label="Enable automatic escalation"
                        />
                        <SwitchNote variant="body2" color="text.secondary">
                            Master switch. When off, no client is auto-escalated even if the per-client toggle is on, and the settings below are locked.
                        </SwitchNote>

                        <MinuteField
                            id="acknowledge-after-label"
                            label="Acknowledge after"
                            value={escalation.acknowledgeAfterMinutes}
                            onChange={(v) => setEscalationField('acknowledgeAfterMinutes', v)}
                            description="Business minutes an unanswered client comment waits before it gets a public acknowledgement."
                            error={timingErrors.acknowledgeAfterMinutes}
                            disabled={escalationDisabled}
                        />
                        <MinuteField
                            id="escalate-after-label"
                            label="Escalate after"
                            value={escalation.escalateAfterMinutes}
                            onChange={(v) => setEscalationField('escalateAfterMinutes', v)}
                            description="Business minutes from the client's comment before the team is escalated to. This is measured from the same moment as the acknowledgement, not added on after it, so it must be larger than the acknowledgement wait."
                            error={timingErrors.escalateAfterMinutes}
                            disabled={escalationDisabled}
                        />
                        <MinuteField
                            id="pm-reminder-label"
                            label="PM reminder cadence"
                            value={escalation.pmNudgeIntervalMinutes}
                            onChange={(v) => setEscalationField('pmNudgeIntervalMinutes', v)}
                            description="How often the account's project manager gets an internal-only reminder while the comment stays unanswered. Only that one person is reminded."
                            error={timingErrors.pmNudgeIntervalMinutes}
                            disabled={escalationDisabled}
                        />
                        <MinuteField
                            id="client-update-label"
                            label="Client update cadence"
                            value={escalation.clientUpdateIntervalMinutes}
                            onChange={(v) => setEscalationField('clientUpdateIntervalMinutes', v)}
                            description="Once escalated, how often a public status update posts to the client."
                            error={timingErrors.clientUpdateIntervalMinutes}
                            disabled={escalationDisabled}
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    type="number"
                                    fullWidth
                                    label="Maximum automated posts"
                                    value={escalation.maxClientPosts}
                                    onChange={(e) => setEscalationField('maxClientPosts', e.target.value)}
                                    inputProps={{ min: 1, step: 1 }}
                                    disabled={escalationDisabled}
                                    error={!!timingErrors.maxClientPosts}
                                    helperText={timingErrors.maxClientPosts || 'Total automated public comments allowed on one thread, counting the acknowledgement as the first'}
                                />
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Autocomplete
                                    value={selectedServiceAccount}
                                    onChange={(_, value) => setEscalationField('postingServiceAccount', value?._id || null)}
                                    options={serviceAccounts}
                                    loading={serviceAccountsLoading}
                                    disabled={escalationDisabled}
                                    getOptionLabel={(a) => personName(a) || a?.email || 'Unnamed service account'}
                                    isOptionEqualToValue={(a, b) => a._id === b._id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Posting account"
                                            error={!!completenessErrors.postingServiceAccount}
                                            helperText={completenessErrors.postingServiceAccount || 'The service account whose name appears on automated public comments'}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Escalation recipients
                        </Typography>
                        <SectionSubtitle variant="body2" color="text.secondary">
                            These people get an internal notification the moment a comment escalates. This is separate from the assigned PM, who is always notified. Only internal
                            managers and administrators can be chosen — clients are never notified that another client is waiting. The end-of-day digest below is sent to this same
                            list.
                        </SectionSubtitle>
                        <Autocomplete
                            multiple
                            value={selectedRecipients}
                            onChange={(_, value) =>
                                setEscalationField(
                                    'escalationRecipients',
                                    value.map((u) => String(u._id)),
                                )
                            }
                            options={recipientOptions}
                            filterSelectedOptions
                            loading={recipientsLoading}
                            disabled={recipientsDisabled}
                            getOptionLabel={recipientLabel}
                            isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
                            renderOption={(props, option) => {
                                const { key, ...rest } = props;
                                return (
                                    <OptionRow key={option._id} {...rest}>
                                        <OptionAvatar src={getAvatarUrl(option)}>
                                            {option.first_name?.charAt(0)}
                                            {option.last_name?.charAt(0)}
                                        </OptionAvatar>
                                        {recipientLabel(option)}
                                    </OptionRow>
                                );
                            }}
                            renderTags={(value, getTagProps) => value.map((option, index) => <Chip label={recipientLabel(option)} {...getTagProps({ index })} key={option._id} />)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Notify on escalation"
                                    placeholder="Add a person"
                                    error={!!completenessErrors.escalationRecipients}
                                    helperText={
                                        completenessErrors.escalationRecipients || 'Only these people are notified when a comment escalates — usually leadership who should know a client is not being responded to'
                                    }
                                />
                            )}
                        />
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            End-of-day checklist
                        </Typography>
                        <SectionSubtitle variant="body2" color="text.secondary">
                            A private reminder each employee receives in Pollux shortly before their own workday ends. It lists the client comments they have not answered yet, their
                            tasks due today or already overdue, and any timers they left running. Each person only ever sees their own checklist.
                        </SectionSubtitle>
                        <FormControlLabel
                            control={<Switch checked={escalation.eodChecklistEnabled} onChange={(e) => setEscalationField('eodChecklistEnabled', e.target.checked)} />}
                            label="Send employees an end-of-day checklist"
                        />
                        <SwitchNote variant="body2" color="text.secondary">
                            When off, no checklists are generated or sent. This runs independently of the automatic escalation switch above.
                        </SwitchNote>

                        <MinuteField
                            id="eod-lead-label"
                            label="Send this far ahead"
                            value={escalation.eodChecklistLeadMinutes}
                            onChange={(v) => setEscalationField('eodChecklistLeadMinutes', v)}
                            description="How long before each person's own working day ends the checklist is sent, using the working hours and timezone on their profile. Anyone without working hours set falls back to the company business hours at the top of this page. On a short working day the checklist still waits until that person's day has started."
                            error={eodErrors.eodChecklistLeadMinutes}
                            disabled={!escalation.eodChecklistEnabled}
                            min={MIN_EOD_LEAD_MINUTES}
                        />

                        <FormControlLabel
                            control={<Switch checked={escalation.eodDigestEnabled} onChange={(e) => setEscalationField('eodDigestEnabled', e.target.checked)} />}
                            label="Email a daily digest of everyone's outstanding items"
                        />
                        <SwitchNote variant="body2" color="text.secondary">
                            One summary email of the team's unanswered comments, overdue tasks and running timers, sent to the people listed under Escalation recipients above. Each
                            copy is filtered to what that person is allowed to see, so two recipients can get different lists; long lists are shortened with an “… and N more” line
                            while the stated totals stay exact. It is sent under an active account holding the billing permission (canRunBilling) — without one nothing goes out, so
                            grant it under Roles Management first.
                        </SwitchNote>
                        {eodErrors.eodDigestEnabled && (
                            <SwitchError variant="caption" color="error">
                                {eodErrors.eodDigestEnabled}
                            </SwitchError>
                        )}
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Message templates
                        </Typography>
                        <SectionSubtitle variant="body2" color="text.secondary">
                            What the client sees. These post publicly on the project, using the sender chosen above.
                        </SectionSubtitle>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label="Acknowledgement message"
                                    value={escalation.acknowledgementTemplate}
                                    onChange={(e) => setEscalationField('acknowledgementTemplate', e.target.value)}
                                    disabled={escalationDisabled}
                                    error={!!completenessErrors.acknowledgementTemplate}
                                    helperText={
                                        completenessErrors.acknowledgementTemplate ||
                                        'Posted once, the first time a comment goes unanswered long enough to trigger an acknowledgement'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label="Status update message"
                                    value={escalation.statusUpdateTemplate}
                                    onChange={(e) => setEscalationField('statusUpdateTemplate', e.target.value)}
                                    disabled={escalationDisabled}
                                    error={!!completenessErrors.statusUpdateTemplate}
                                    helperText={
                                        completenessErrors.statusUpdateTemplate ||
                                        'Posted on a recurring cadence once a comment has escalated, until someone responds or the post limit is reached'
                                    }
                                />
                            </Grid>
                        </Grid>
                        <TokenCard>
                            <Typography variant="subtitle2" gutterBottom>
                                Placeholders (click to copy)
                            </Typography>
                            {PLACEHOLDER_TOKENS.map(({ token, description }) => (
                                <TokenRow key={token} type="button" aria-label={`Copy the placeholder ${token} to the clipboard`} onClick={() => handleCopyToken(token)}>
                                    <ContentCopyIcon fontSize="small" color="action" />
                                    <TokenCode variant="body2">{token}</TokenCode>
                                    <Typography variant="caption" color="text.secondary">
                                        {description}
                                    </Typography>
                                </TokenRow>
                            ))}
                        </TokenCard>
                    </SectionCard>

                    <ActionsRow>
                        <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </ActionsRow>
                </>
            )}

            <ConfirmDialog
                open={showPrompt}
                onClose={cancelLeave}
                onConfirm={confirmLeave}
                title="Unsaved Changes"
                message={unsavedMessage}
                confirmLabel="Leave"
                confirmColor="warning"
            />
        </AdminPageContainer>
    );
};

export default EscalationSettings;
